import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { AI_SPEND_TOOLTIP, AiSpendIcon } from "../ai-spend";
import type { StandardSetSummary } from "../../auth/api-client";
import LessonAiProgressPanel from "../LessonAiProgressPanel";
import type { LessonAiProgressUpdate } from "../lesson-ai-progress";
import { MentionInput } from "../standards-mentions/MentionInput";
import type { MentionPromptDoc } from "../standards-mentions/mentionTypes";
import { isMentionDocEmpty } from "../standards-mentions/mentionTypes";

export interface LessonAiAssistPanelProps {
  readonly variant: "empty" | "inline";
  readonly hasContent: boolean;
  readonly bearer: string | null;
  readonly mentionDoc: MentionPromptDoc;
  readonly onMentionDocChange: (doc: MentionPromptDoc) => void;
  readonly standardSetId: string;
  readonly onStandardSetIdChange: (id: string) => void;
  readonly standardSets: readonly StandardSetSummary[];
  readonly aiBusy: boolean;
  readonly aiProgressMode: "generate" | "refine" | null;
  readonly aiProgress: LessonAiProgressUpdate | null;
  readonly blockedByAdvancedPrompt: boolean;
  readonly onGenerate: () => void;
  readonly onRefine: () => void;
  readonly onStartBlank?: () => void;
}

export function LessonAiAssistPanel({
  variant,
  hasContent,
  bearer,
  mentionDoc,
  onMentionDocChange,
  standardSetId,
  onStandardSetIdChange,
  standardSets,
  aiBusy,
  aiProgressMode,
  aiProgress,
  blockedByAdvancedPrompt,
  onGenerate,
  onRefine,
  onStartBlank,
}: LessonAiAssistPanelProps) {
  const placeholder = hasContent
    ? 'Describe a change. Type @ to tag a standard or glossary term.'
    : "Describe the lesson you want. Type @ to tag a standard or glossary term.";

  const promptDisabledActions =
    aiBusy || blockedByAdvancedPrompt || isMentionDocEmpty(mentionDoc);

  return (
    <section className={`lesson-ws-ai-panel lesson-ws-ai-panel--${variant}`}>
      <div className="lesson-ws-ai-head">
        <FontAwesomeIcon icon={faWandMagicSparkles} className="lesson-ws-ai-icon" />
        <div>
          <h2 className="lesson-ws-ai-title">
            {variant === "empty" ? "Start with AI" : "AI assist"}
          </h2>
          <p className="muted small lesson-ws-ai-lede">
            {hasContent
              ? "Describe a change and Evolve to build on the current lesson, or Replace to regenerate from your prompt."
              : "Describe the lesson you want and generate a full draft (code, instructions, and checks), then edit anything."}
          </p>
        </div>
      </div>

      <MentionInput
        bearer={bearer}
        value={mentionDoc}
        onChange={onMentionDocChange}
        standardSetId={standardSetId}
        onStandardSetIdChange={onStandardSetIdChange}
        standardSets={standardSets}
        showSetSelector={standardSets.length > 1}
        disabled={aiBusy}
        minRows={variant === "empty" ? 4 : 3}
        placeholder={placeholder}
      />

      <div className="teacher-toolbar-form lesson-ws-ai-actions">
        {hasContent ? (
          <>
            <button
              type="button"
              className="btn primary"
              disabled={promptDisabledActions}
              title={AI_SPEND_TOOLTIP}
              onClick={onRefine}
            >
              <AiSpendIcon /> {aiBusy && aiProgressMode === "refine" ? "Evolving…" : "Evolve with prompt"}
            </button>
            <button
              type="button"
              className="btn secondary"
              disabled={promptDisabledActions}
              title={AI_SPEND_TOOLTIP}
              onClick={onGenerate}
            >
              <AiSpendIcon /> {aiBusy && aiProgressMode === "generate" ? "Replacing…" : "Replace from prompt"}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn primary"
              disabled={promptDisabledActions}
              title={AI_SPEND_TOOLTIP}
              onClick={onGenerate}
            >
              <AiSpendIcon /> {aiBusy ? "Generating…" : "Generate lesson"}
            </button>
            {onStartBlank ? (
              <button type="button" className="btn secondary" disabled={aiBusy} onClick={onStartBlank}>
                Start blank instead
              </button>
            ) : null}
          </>
        )}
      </div>

      {hasContent ? (
        <p className="muted small" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
          <strong>Evolve</strong> keeps what works and applies your change. <strong>Replace</strong>{" "}
          regenerates the whole draft (still seeded with your current fields).
        </p>
      ) : null}

      {aiBusy && aiProgress && aiProgressMode ? (
        <div className="lesson-ws-ai-progress">
          <LessonAiProgressPanel mode={aiProgressMode} progress={aiProgress} />
        </div>
      ) : null}
    </section>
  );
}
